import { z } from 'zod';
import { moneySchema } from '../money';
import { sourcingSchema } from '../inventory/article.schema';
import { projectGroupSchema } from '../projects/calculation.schema';

/**
 * Analyse-Modul (ADR 0042): gezielte, parametrisierte Abfragen über Artikel,
 * Belege und Projekte — serverseitig aggregiert (kein 200er-Listen-Cap wie im
 * client-seitigen Cockpit). Alle Geldbeträge als Money (bigint-Mikro-Einheiten,
 * string-serialisiert) — §6, keine Float-Summen.
 */

/** ISO-Datum (JJJJ-MM-TT) für Zeitraum-Filter. */
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet JJJJ-MM-TT');

/**
 * Boolean aus einem Query-String. Bewusst `'true'|'false'` OHNE `.transform`:
 * `z.coerce.boolean()` macht aus dem String „false" ein `true`, und ein
 * `transform` würde Ein-/Ausgabetyp trennen, was die `ZodValidationPipe`
 * (`ZodSchema<T>` = gleicher Ein-/Ausgabetyp) nicht annimmt. Umgewandelt wird
 * in `selectionPoolInputFromExportQuery` (Muster: `projectListQuerySchema.includeArchived`).
 */
const booleanQuerySchema = z.enum(['true', 'false']);

/** Gemeinsamer Zeitraum-Filter; ohne Angabe: die letzten 12 Monate. */
export const analyticsRangeQuerySchema = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type AnalyticsRangeQuery = z.infer<typeof analyticsRangeQuerySchema>;

// ── 1) Artikel: Lagerbewegungen + Einkaufsvolumen ──────────────────────────────

export const articleAnalysisQuerySchema = analyticsRangeQuerySchema.extend({
  /** Auf einen Artikel eingrenzen (sonst alle). */
  articleId: z.string().uuid().optional(),
});
export type ArticleAnalysisQuery = z.infer<typeof articleAnalysisQuerySchema>;

/** Ein Monatspunkt der Artikel-Zeitreihe (Zugang/Abgang aus Lagerbewegungen + Verkauf). */
export const articleMonthPointSchema = z.object({
  /** Monats-Schlüssel `JJJJ-MM` (chronologisch sortierbar). */
  month: z.string(),
  /** Anzeige-Label, z. B. „Jan 26". */
  label: z.string(),
  inQty: z.number(),
  outQty: z.number(),
  /** Wert der Abgänge (Σ Menge × EK-Snapshot der Bewegung). */
  outValue: moneySchema,
  /** Gelieferte Menge (Lieferschein-Positionen, 2026-07-30 — s. Modul-Doku). */
  deliveredQty: z.number(),
  /** Umsatz (Zeilen-Netto der Rechnungspositionen — INVOICE/ADVANCE_INVOICE/FINAL_INVOICE). */
  salesValue: moneySchema,
});
export type ArticleMonthPoint = z.infer<typeof articleMonthPointSchema>;

export const topArticleRowSchema = z.object({
  articleId: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  inQty: z.number(),
  outQty: z.number(),
  outValue: moneySchema,
  /** Bestellte Menge/Wert im Zeitraum (Bestellungen, nicht storniert). */
  purchasedQty: z.number(),
  purchasedValue: moneySchema,
  /** Gelieferte Menge im Zeitraum (Lieferschein-Positionen, s. o.). */
  deliveredQty: z.number(),
  /** Umsatz im Zeitraum (Rechnungs-Belege, s. o.). */
  salesValue: moneySchema,
});
export type TopArticleRow = z.infer<typeof topArticleRowSchema>;

/** Ø-EK je Monat (Σ Wert ÷ Σ Menge der Bestellpositionen); null = keine Käufe. */
export const articlePriceTrendPointSchema = z.object({
  month: z.string(),
  label: z.string(),
  avgPrice: moneySchema.nullable(),
});
export type ArticlePriceTrendPoint = z.infer<typeof articlePriceTrendPointSchema>;

/** Bestand am Monatsende (Vorwärts-Replay aller Bewegungen; ADJUST setzt absolut). */
export const articleStockPointSchema = z.object({
  month: z.string(),
  label: z.string(),
  endQty: z.number(),
});
export type ArticleStockPoint = z.infer<typeof articleStockPointSchema>;

/** Einkaufsvolumen eines Lieferanten für den Artikel im Zeitraum. */
export const articleSupplierSliceSchema = z.object({
  supplierId: z.string().uuid().nullable(),
  name: z.string(),
  qty: z.number(),
  value: moneySchema,
});
export type ArticleSupplierSlice = z.infer<typeof articleSupplierSliceSchema>;

/** Tiefen-Detail eines einzelnen Artikels (nur bei articleId-Filter). */
export const articleDetailSchema = z.object({
  priceTrend: z.array(articlePriceTrendPointSchema),
  stockTrend: z.array(articleStockPointSchema),
  bySupplier: z.array(articleSupplierSliceSchema),
});
export type ArticleDetail = z.infer<typeof articleDetailSchema>;

export const articleAnalysisSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  currency: z.enum(['EUR', 'USD', 'CHF']),
  months: z.array(articleMonthPointSchema),
  totals: z.object({
    inQty: z.number(),
    outQty: z.number(),
    outValue: moneySchema,
    purchasedValue: moneySchema,
    /** Umsatz (Rechnungspositionen) im Zeitraum, alle Artikel zusammen. */
    salesValue: moneySchema,
  }),
  /** Top-Artikel nach Abgangswert (bzw. -menge) im Zeitraum. */
  topArticles: z.array(topArticleRowSchema),
  /** Tiefen-Detail (Preis-/Bestandsverlauf, Lieferanten) — nur mit articleId-Filter. */
  detail: articleDetailSchema.nullable(),
});
export type ArticleAnalysis = z.infer<typeof articleAnalysisSchema>;

// ── 2) Belege: Wert/Anzahl je Monat nach Belegart ─────────────────────────────

export const documentScopeSchema = z.enum(['SALES', 'PURCHASE']);
export type DocumentScope = z.infer<typeof documentScopeSchema>;

export const documentAnalysisQuerySchema = analyticsRangeQuerySchema.extend({
  scope: documentScopeSchema.default('SALES'),
  /** Auf eine Belegart eingrenzen (z. B. INVOICE, BESTELLUNG); sonst alle. */
  kind: z.string().min(1).max(40).optional(),
  /** Auf einen Kunden (SALES) bzw. Lieferanten (PURCHASE) eingrenzen. */
  partnerId: z.string().uuid().optional(),
});
export type DocumentAnalysisQuery = z.infer<typeof documentAnalysisQuerySchema>;

/** Wert/Anzahl einer Belegart innerhalb eines Monats. */
export const documentKindSliceSchema = z.object({
  kind: z.string(),
  count: z.number().int(),
  net: moneySchema,
});
export type DocumentKindSlice = z.infer<typeof documentKindSliceSchema>;

export const documentMonthPointSchema = z.object({
  month: z.string(),
  label: z.string(),
  byKind: z.array(documentKindSliceSchema),
  count: z.number().int(),
  net: moneySchema,
});
export type DocumentMonthPoint = z.infer<typeof documentMonthPointSchema>;

export const topPartnerRowSchema = z.object({
  partnerId: z.string().uuid().nullable(),
  name: z.string(),
  count: z.number().int(),
  net: moneySchema,
});
export type TopPartnerRow = z.infer<typeof topPartnerRowSchema>;

export const documentAnalysisSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  scope: documentScopeSchema,
  currency: z.enum(['EUR', 'USD', 'CHF']),
  /** Alle im Zeitraum vorkommenden Belegarten (Reihenfolge = Chart-Stapel). */
  kinds: z.array(z.string()),
  months: z.array(documentMonthPointSchema),
  totals: z.object({ count: z.number().int(), net: moneySchema }),
  /** Top-Kunden bzw. -Lieferanten nach Nettowert im Zeitraum. */
  topPartners: z.array(topPartnerRowSchema),
});
export type DocumentAnalysis = z.infer<typeof documentAnalysisSchema>;

// ── 3) Projekte: fakturiert vs. bestellt je Monat ─────────────────────────────

export const projectAnalysisQuerySchema = analyticsRangeQuerySchema.extend({
  /** Auf einen Projektstatus eingrenzen (sonst alle). */
  status: z.string().min(1).max(40).optional(),
  /** Einzelprojekt-Tiefenanalyse: nur dieses Projekt auswerten. */
  projectId: z.string().uuid().optional(),
});
export type ProjectAnalysisQuery = z.infer<typeof projectAnalysisQuerySchema>;

export const projectMonthPointSchema = z.object({
  month: z.string(),
  label: z.string(),
  /** Fakturiert = projektbezogene Rechnungs-Belege (netto). */
  invoiced: moneySchema,
  /** Bestellt = projektbezogene Bestellungen (netto, nicht storniert). */
  purchased: moneySchema,
  /** Entnommen = projektbezogene Lagerabgänge, bewertet mit dem EK-Snapshot. */
  withdrawn: moneySchema,
});
export type ProjectMonthPoint = z.infer<typeof projectMonthPointSchema>;

export const topProjectRowSchema = z.object({
  projectId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  status: z.string(),
  orderValue: moneySchema.nullable(),
  invoiced: moneySchema,
  purchased: moneySchema,
  withdrawn: moneySchema,
});
export type TopProjectRow = z.infer<typeof topProjectRowSchema>;

export const projectAnalysisSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  currency: z.enum(['EUR', 'USD', 'CHF']),
  months: z.array(projectMonthPointSchema),
  totals: z.object({
    projectCount: z.number().int(),
    invoiced: moneySchema,
    purchased: moneySchema,
    withdrawn: moneySchema,
  }),
  /** Top-Projekte nach Fakturawert im Zeitraum. */
  topProjects: z.array(topProjectRowSchema),
});
export type ProjectAnalysis = z.infer<typeof projectAnalysisSchema>;

// ── 4) Selektionspool: kuratierte Filter-Abfragen je Entität (ADR 0046) ────────
//
// Keine generische Query-Engine (Sicherheitsrisiko ohne Tenant-Middleware,
// ADR 0046-Begründung) — je Entität ein fest kuratiertes Filter-Set. Antwort
// immer „Liste + Kennzahlen" (Nutzer-Entscheid): `items` (Zeilen) + `kpis`
// (Kachel-Kennzahlen, Geld strukturiert als Money — §6, nie vorformatiert).

export const selectionPoolEntitySchema = z.enum(['customer', 'article', 'document', 'contact', 'project']);
export type SelectionPoolEntity = z.infer<typeof selectionPoolEntitySchema>;

/**
 * Die Teilmenge der Entitäten, die im **persistenten** Selektionspool (ADR 0047)
 * gespeichert werden kann. Bewusst enger als `selectionPoolEntitySchema`:
 * `contact`/`project` (Nachtrag 2026-08-13 zu ADR 0046) sind reine Auswertungs-
 * Modi — sie haben keine `SelectionPoolMemberKind`-Entsprechung in Prisma und
 * sollen NICHT poolbar werden. Ohne diesen engeren Typ würde die Erweiterung
 * der Abfrage-Entitäten den Pool-Code stillschweigend mit-erweitern
 * (`resolveSelectionItems` fiele für unbekannte Arten in den Belege-Zweig).
 */
export const poolableSelectionPoolEntitySchema = z.enum(['customer', 'article', 'document']);
export type PoolableSelectionPoolEntity = z.infer<typeof poolableSelectionPoolEntitySchema>;

/** Beleg-Bereich für den Belege-Modus (ADR 0047): Verkauf oder Einkauf. */
export const documentPoolScopeSchema = z.enum(['SALES', 'PURCHASE']);
export type DocumentPoolScope = z.infer<typeof documentPoolScopeSchema>;

/**
 * Land-Filter (Kunde/Ansprechpartner). Kunden tragen ZWEI Landfelder: `country`
 * (ISO-2, z. B. „HR") und `countryName` (Klartext, z. B. „Kroatien"). Nutzer
 * tippen mal das eine, mal das andere — deshalb nimmt der Filter beides an
 * (Auflösung im Backend, s. `countryFilterCondition` im Analytics-Service):
 * bis 2 Zeichen ISO-Vergleich, ab 3 Zeichen zusätzlich Klartext-Teiltreffer.
 */
const countryFilterSchema = z.string().trim().min(1).max(60);

/** Kunde-Filter: Adresse + Gesellschaft + Umsatz-Zeitraum (Rechnungs-Belege). */
export const customerPoolFiltersSchema = z.object({
  /** Ort, contains (case-insensitive). */
  city: z.string().trim().min(1).max(120).optional(),
  /** PLZ, contains. */
  postalCode: z.string().trim().min(1).max(20).optional(),
  /** Land: ISO-2 („HR") ODER Klartext („Kroatien") — s. `countryFilterSchema`. */
  country: countryFilterSchema.optional(),
  /** Betreuende Gesellschaft (`ownerLegalEntityId`), equals. */
  legalEntityId: z.string().uuid().optional(),
  /** Umsatz-Zeitraum (ISO-Datum); ohne Angabe: gesamter Zeitraum (kein Cut-off). */
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  /**
   * „inkl. Ansprechpartner" (2026-08-13): hängt je Kunde seine Ansprechpartner
   * an die Ergebniszeile. **LEFT-JOIN-Semantik** — Kunden OHNE Ansprechpartner
   * bleiben in der Liste (mit leerer `contacts`-Liste). Wer eine reine
   * Personenliste will, nimmt `entity: 'contact'`.
   */
  includeContacts: z.boolean().optional(),
});
export type CustomerPoolFilters = z.infer<typeof customerPoolFiltersSchema>;

/**
 * Ansprechpartner-Filter (2026-08-13). Fachlicher Kern: ein `Contact` hat KEINE
 * eigene Adresse — Land/Ort/PLZ hängen am zugehörigen `Customer`. „Alle
 * Ansprechpartner in Mexiko" ist deshalb ein Filter über den Kunden des
 * Ansprechpartners, nicht über den Ansprechpartner selbst.
 */
export const contactPoolFiltersSchema = z.object({
  /** Land des KUNDEN: ISO-2 ODER Klartext (s. `countryFilterSchema`). */
  country: countryFilterSchema.optional(),
  /** Ort des KUNDEN, contains (case-insensitive). */
  city: z.string().trim().min(1).max(120).optional(),
  /** PLZ des KUNDEN, contains. */
  postalCode: z.string().trim().min(1).max(20).optional(),
  /** Sprache des Ansprechpartners, equals (case-insensitive) — kurzes Codefeld („DE"). */
  language: z.string().trim().min(1).max(40).optional(),
  /** Abteilung des Ansprechpartners, contains (case-insensitive). */
  department: z.string().trim().min(1).max(120).optional(),
  /** Position/Funktion des Ansprechpartners, contains (case-insensitive). */
  position: z.string().trim().min(1).max(120).optional(),
  /** Nur Hauptansprechpartner (`isPrimary`). */
  isPrimaryOnly: z.boolean().optional(),
  /** Betreuende Gesellschaft des KUNDEN (`ownerLegalEntityId`), equals. */
  legalEntityId: z.string().uuid().optional(),
});
export type ContactPoolFilters = z.infer<typeof contactPoolFiltersSchema>;

/**
 * Projekt-/Referenzen-Filter (2026-08-13) — dieselben Merkmale wie die
 * Referenzen-Ansicht des Projektmoduls. `country` ist am Projekt ein
 * KLARTEXT-Feld (kein ISO-Code wie beim Kunden) → Teiltreffer, damit „AUT"
 * und „Österreich" gleichermaßen funktionieren. Baujahr-Treffer, wenn
 * Start- ODER Liefertermin im Jahresbereich liegt (gleiche Regel wie die
 * Referenzen-Liste).
 */
export const projectPoolFiltersSchema = z.object({
  /** Land (Klartext am Projekt), contains (case-insensitive). */
  country: countryFilterSchema.optional(),
  /** Anlagentyp, contains (case-insensitive). */
  plantType: z.string().trim().min(1).max(120).optional(),
  /** Produkt, contains (case-insensitive). */
  product: z.string().trim().min(1).max(200).optional(),
  /** Projektgruppe/Branche (KOMMUNAL | INDUSTRIE), equals. */
  projectGroup: projectGroupSchema.optional(),
  yearFrom: z.number().int().min(1900).max(2100).optional(),
  yearTo: z.number().int().min(1900).max(2100).optional(),
});
export type ProjectPoolFilters = z.infer<typeof projectPoolFiltersSchema>;

/**
 * Artikel-Filter: Volltext + Material (Hybrid-Auflösung, s. Backend-Service) +
 * Warengruppe/Beschaffungsart. KEIN Gesellschafts-/Zeitraum-Feld — Artikel sind
 * gesellschaftsübergreifende Stammdaten (ADR 0029), nicht anwendbar.
 */
export const articlePoolFiltersSchema = z.object({
  /** Volltext: Artikelnummer/Name/Beschreibung (contains). */
  q: z.string().trim().min(1).max(200).optional(),
  /** Material: Materialname (→ Code-Auflösung) ODER Artikel-Textfelder (contains), ein Treffer reicht. */
  material: z.string().trim().min(1).max(200).optional(),
  commodityGroup: z.number().int().positive().optional(),
  sourcing: sourcingSchema.optional(),
});
export type ArticlePoolFilters = z.infer<typeof articlePoolFiltersSchema>;

/**
 * Belege-Filter (ADR 0047): Bereich (Verkauf/Einkauf) + Belegart + Partner +
 * Zeitraum + Volltext auf der Belegnummer. Datumsfeld je Bereich: Verkauf
 * `documentDate`, Einkauf `orderDate` (im Service aufgelöst).
 */
export const documentPoolFiltersSchema = z.object({
  scope: documentPoolScopeSchema.default('SALES'),
  /** Belegart, z. B. INVOICE / BESTELLUNG (gegen die Arten des Bereichs geprüft). */
  kind: z.string().trim().min(1).max(40).optional(),
  /** Partner: customerId (Verkauf) bzw. supplierId (Einkauf), equals. */
  partnerId: z.string().uuid().optional(),
  /** Volltext auf der Belegnummer (contains, case-insensitive). */
  q: z.string().trim().min(1).max(60).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
});
export type DocumentPoolFilters = z.infer<typeof documentPoolFiltersSchema>;

/** Anfrage-Payload: `entity` bestimmt, welches Filter-Set gilt (discriminated union). */
export const selectionPoolInputSchema = z.discriminatedUnion('entity', [
  z.object({ entity: z.literal('customer'), filters: customerPoolFiltersSchema.default({}) }),
  z.object({ entity: z.literal('article'), filters: articlePoolFiltersSchema.default({}) }),
  z.object({ entity: z.literal('document'), filters: documentPoolFiltersSchema.default({ scope: 'SALES' }) }),
  z.object({ entity: z.literal('contact'), filters: contactPoolFiltersSchema.default({}) }),
  z.object({ entity: z.literal('project'), filters: projectPoolFiltersSchema.default({}) }),
]);
export type SelectionPoolInput = z.infer<typeof selectionPoolInputSchema>;

/** Ansprechpartner-Kurzform am Kunden-Item (nur bei `includeContacts`). */
export const selectionPoolCustomerContactSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  position: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  isPrimary: z.boolean(),
});
export type SelectionPoolCustomerContact = z.infer<typeof selectionPoolCustomerContactSchema>;

/** Kunde-Ergebniszeile: Stammdaten + Umsatz im gewählten Zeitraum (0, falls keiner). */
export const selectionPoolCustomerItemSchema = z.object({
  kind: z.literal('customer'),
  id: z.string().uuid(),
  name: z.string(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  countryName: z.string().nullable(),
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  revenue: moneySchema,
  invoiceCount: z.number().int(),
  /** Ansprechpartner des Kunden — LEER, wenn `includeContacts` nicht gesetzt ist. */
  contacts: z.array(selectionPoolCustomerContactSchema),
});
export type SelectionPoolCustomerItem = z.infer<typeof selectionPoolCustomerItemSchema>;

/**
 * Ansprechpartner-Ergebniszeile (2026-08-13): Person + Adresse des zugehörigen
 * Kunden (der Ansprechpartner selbst hat keine — s. `contactPoolFiltersSchema`).
 */
export const selectionPoolContactItemSchema = z.object({
  kind: z.literal('contact'),
  id: z.string().uuid(),
  salutation: z.string().nullable(),
  title: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  position: z.string().nullable(),
  department: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
  language: z.string().nullable(),
  /**
   * Hauptansprechpartner des Kunden. Nach `isPrimaryOnly` ließ sich von Anfang an
   * filtern — sichtbar war es in der Trefferliste aber nicht, obwohl der Wert
   * intern schon die Sortierung steuert. Genau das will man in einer
   * Personenliste sehen: „wen rufe ich bei dieser Firma zuerst an".
   */
  isPrimary: z.boolean(),
  /** Kunde/Adresse, an dem der Ansprechpartner hängt. */
  customerId: z.string().uuid(),
  customerName: z.string(),
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  countryName: z.string().nullable(),
});
export type SelectionPoolContactItem = z.infer<typeof selectionPoolContactItemSchema>;

/** Projekt-/Referenz-Ergebniszeile (2026-08-13). */
export const selectionPoolProjectItemSchema = z.object({
  kind: z.literal('project'),
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  customerName: z.string().nullable(),
  /** Land am Projekt (Klartext — anders als beim Kunden kein ISO-Code). */
  country: z.string().nullable(),
  /**
   * Klartext-Land: am Projekt gibt es kein eigenes Feld dafür, deshalb aus dem
   * verknüpften Kunden übernommen (null, wenn kein Kunde/kein Wert).
   */
  countryName: z.string().nullable(),
  location: z.string().nullable(),
  plantType: z.string().nullable(),
  product: z.string().nullable(),
  /** Baujahr aus `startDate` bzw. `deliveryDate` (null, wenn nicht gepflegt). */
  startYear: z.number().int().nullable(),
  deliveryYear: z.number().int().nullable(),
});
export type SelectionPoolProjectItem = z.infer<typeof selectionPoolProjectItemSchema>;

/** Artikel-Ergebniszeile: Stammdaten + Lagerwert (Bestand × EK). */
export const selectionPoolArticleItemSchema = z.object({
  kind: z.literal('article'),
  id: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  commodityGroup: z.number().int().nullable(),
  sourcing: sourcingSchema,
  stockQty: z.number(),
  stockValue: moneySchema,
});
export type SelectionPoolArticleItem = z.infer<typeof selectionPoolArticleItemSchema>;

/**
 * Beleg-Ergebniszeile (ADR 0047). `kind` unterscheidet Verkauf/Einkauf (die
 * beiden Belege in verschiedenen Tabellen), `scope` gruppiert die Kennzahlen.
 */
export const selectionPoolDocumentItemSchema = z.object({
  kind: z.enum(['sales_document', 'purchase_document']),
  scope: documentPoolScopeSchema,
  id: z.string().uuid(),
  number: z.string(),
  /** Belegart (INVOICE, BESTELLUNG, …). */
  documentKind: z.string(),
  status: z.string(),
  /** Kunde (Verkauf) bzw. Lieferant (Einkauf). */
  partnerName: z.string().nullable(),
  /** Belegdatum (Verkauf `documentDate`, Einkauf `orderDate`) als ISO-Datum. */
  date: z.string(),
  netAmount: moneySchema,
  legalEntityName: z.string().nullable(),
});
export type SelectionPoolDocumentItem = z.infer<typeof selectionPoolDocumentItemSchema>;

/**
 * Zeilen, die im **persistenten** Pool (ADR 0047) vorkommen können — bewusst
 * NUR die drei poolbaren Arten. `contact`/`project` sind reine Auswertungs-
 * Modi (Nachtrag 2026-08-13, s. `poolableSelectionPoolEntitySchema`); sie hier
 * aufzunehmen würde die Pool-Anzeige (`SelectionPoolDetail`) und die Übersetzung
 * „Item → Mitglied" (`MEMBER_KIND_BY_ITEM_KIND`) stillschweigend mit-erweitern.
 * Das Ergebnis der Kandidatensuche ist `selectionPoolResultItemSchema`.
 */
export const selectionPoolItemSchema = z.union([
  selectionPoolCustomerItemSchema,
  selectionPoolArticleItemSchema,
  selectionPoolDocumentItemSchema,
]);
export type SelectionPoolItem = z.infer<typeof selectionPoolItemSchema>;

/** Alle Zeilentypen, die die Abfrage `POST /v1/analytics/selection-pool` liefern kann. */
export const selectionPoolResultItemSchema = z.union([
  selectionPoolCustomerItemSchema,
  selectionPoolArticleItemSchema,
  selectionPoolDocumentItemSchema,
  selectionPoolContactItemSchema,
  selectionPoolProjectItemSchema,
]);
export type SelectionPoolResultItem = z.infer<typeof selectionPoolResultItemSchema>;

/** Kachel-Kennzahl: Anzahl (Zahl) oder Geldsumme (Money, strukturiert — §6). */
export const selectionPoolKpiSchema = z.discriminatedUnion('unit', [
  z.object({ key: z.string(), label: z.string(), unit: z.literal('count'), value: z.number().int() }),
  z.object({ key: z.string(), label: z.string(), unit: z.literal('money'), value: moneySchema }),
]);
export type SelectionPoolKpi = z.infer<typeof selectionPoolKpiSchema>;

export const selectionPoolResultSchema = z.object({
  entity: selectionPoolEntitySchema,
  items: z.array(selectionPoolResultItemSchema),
  kpis: z.array(selectionPoolKpiSchema),
});
export type SelectionPoolResult = z.infer<typeof selectionPoolResultSchema>;

/**
 * PDF-Export derselben Abfrage (2026-08-13):
 * `GET /v1/analytics/selection-pool/export-pdf?entity=contact&country=MX&…`.
 *
 * Bewusst **GET mit flachen Query-Parametern** statt POST mit Body wie die
 * Abfrage selbst: Das Frontend öffnet Dateien nach ADR 0075 über
 * `openFilePreview`, und die Vorschau kann nur GET. Alle Werte kommen als
 * String an → Zahlen/Booleans werden hier gecastet und anschließend über
 * `selectionPoolInputFromExportQuery` in denselben `SelectionPoolInput`
 * übersetzt, den auch der POST-Weg nutzt (eine Filter-Wahrheit, keine zweite).
 */
export const selectionPoolExportQuerySchema = z.object({
  entity: selectionPoolEntitySchema,
  // Kunde + Ansprechpartner
  city: z.string().trim().min(1).max(120).optional(),
  postalCode: z.string().trim().min(1).max(20).optional(),
  country: countryFilterSchema.optional(),
  legalEntityId: z.string().uuid().optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  includeContacts: booleanQuerySchema.optional(),
  // Ansprechpartner
  language: z.string().trim().min(1).max(40).optional(),
  department: z.string().trim().min(1).max(120).optional(),
  position: z.string().trim().min(1).max(120).optional(),
  isPrimaryOnly: booleanQuerySchema.optional(),
  // Artikel
  q: z.string().trim().min(1).max(200).optional(),
  material: z.string().trim().min(1).max(200).optional(),
  commodityGroup: z.coerce.number().int().positive().optional(),
  sourcing: sourcingSchema.optional(),
  // Belege
  scope: documentPoolScopeSchema.optional(),
  kind: z.string().trim().min(1).max(40).optional(),
  partnerId: z.string().uuid().optional(),
  // Projekte
  plantType: z.string().trim().min(1).max(120).optional(),
  product: z.string().trim().min(1).max(200).optional(),
  projectGroup: projectGroupSchema.optional(),
  yearFrom: z.coerce.number().int().min(1900).max(2100).optional(),
  yearTo: z.coerce.number().int().min(1900).max(2100).optional(),
});
export type SelectionPoolExportQuery = z.infer<typeof selectionPoolExportQuerySchema>;

/**
 * Flache Export-Query → `SelectionPoolInput`. Die Filter werden bewusst durch
 * dasselbe `selectionPoolInputSchema` geparst wie beim POST: nicht anwendbare
 * Felder fallen dabei still weg (ADR 0046 — „ignorieren, nicht ablehnen").
 */
export function selectionPoolInputFromExportQuery(query: SelectionPoolExportQuery): SelectionPoolInput {
  const { entity, includeContacts, isPrimaryOnly, ...rest } = query;
  const filters = {
    ...rest,
    ...(includeContacts !== undefined ? { includeContacts: includeContacts === 'true' } : {}),
    ...(isPrimaryOnly !== undefined ? { isPrimaryOnly: isPrimaryOnly === 'true' } : {}),
  };
  return selectionPoolInputSchema.parse({ entity, filters });
}

// ── 5) Berichtscenter: Verkaufsschlager, Ladenhüter, Warengruppen, Kunden (2026-07-30) ──
//
// Kuratierte, benannte Berichte (kein Selbstbau-Query-Builder, dieselbe
// Begründung wie beim Selektionspool). „Gelieferte Menge" kommt bewusst aus
// Lieferschein-Positionen statt Rechnungspositionen (Anweisung Tech-Lead
// 2026-07-30) — s. `docs/modules/analytics.md`.

export const salesReportsQuerySchema = analyticsRangeQuerySchema;
export type SalesReportsQuery = z.infer<typeof salesReportsQuerySchema>;

export const bestsellerRowSchema = z.object({
  articleId: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  /** Gelieferte Menge (Lieferschein-Positionen) im Zeitraum. */
  deliveredQty: z.number(),
  /** Umsatz (Rechnungspositionen) im selben Zeitraum — zur Einordnung, nicht die Sortiergrundlage. */
  salesValue: moneySchema,
});
export type BestsellerRow = z.infer<typeof bestsellerRowSchema>;

export const slowMoverRowSchema = z.object({
  articleId: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  /** Aktueller Bestand. */
  stockQty: z.number(),
  /** Gelieferte Menge im Zeitraum (0 = im Zeitraum unbewegt). */
  deliveredQty: z.number(),
});
export type SlowMoverRow = z.infer<typeof slowMoverRowSchema>;

export const commodityGroupRowSchema = z.object({
  /** Warengruppe (4-stellig, ADR 0016); null = ohne Warengruppe. */
  commodityGroup: z.number().int().nullable(),
  deliveredQty: z.number(),
  salesValue: moneySchema,
});
export type CommodityGroupRow = z.infer<typeof commodityGroupRowSchema>;

export const customerRankingRowSchema = z.object({
  customerId: z.string().uuid().nullable(),
  name: z.string(),
  invoiceCount: z.number().int(),
  net: moneySchema,
});
export type CustomerRankingRow = z.infer<typeof customerRankingRowSchema>;

export const salesReportsSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  currency: z.enum(['EUR', 'USD', 'CHF']),
  /** Top-Artikel nach gelieferter Menge. */
  bestsellers: z.array(bestsellerRowSchema),
  /** Aktive, gelagerte Artikel mit der geringsten Lieferbewegung im Zeitraum. */
  slowMovers: z.array(slowMoverRowSchema),
  /** Menge/Umsatz je Warengruppe, absteigend nach Menge. */
  commodityGroups: z.array(commodityGroupRowSchema),
  /** Top-Kunden nach Rechnungs-Umsatz im Zeitraum. */
  topCustomers: z.array(customerRankingRowSchema),
  /** Gesamtsummen (alle Lieferungen/Belege, nicht nur Top-N) — für „Anteil am Gesamt". */
  totals: z.object({
    deliveredQty: z.number(),
    revenue: moneySchema,
  }),
});
export type SalesReports = z.infer<typeof salesReportsSchema>;
